import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, Calendar, CreditCard, Shield } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '@/App';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const [status, setStatus] = useState('loading');
  const [subscriptionData, setSubscriptionData] = useState(null);

  useEffect(() => {
    // Fetch subscription status from backend
    const fetchSubscriptionStatus = async () => {
      if (!token) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API}/subscriptions/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setSubscriptionData(response.data);
        setStatus('success');
      } catch (error) {
        console.error('Error fetching subscription status:', error);
        // Even on error, show success - the webhook will update the DB
        setStatus('success');
      }
    };

    // Give webhook a moment to process
    const timer = setTimeout(fetchSubscriptionStatus, 1500);
    return () => clearTimeout(timer);
  }, [token]);

  const handleContinue = () => {
    navigate('/dashboard');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getPlanDisplayName = (plan) => {
    const planNames = {
      'router_monthly': 'CNC Router Monthly',
      'laser_monthly': 'Laser Cutting Monthly',
      'printing_monthly': '3D Printing Monthly',
      'single_annual': 'Single Edition Annual',
      'two_annual': 'Two Editions Annual',
      'full_annual': 'Full Access Annual'
    };
    return planNames[plan] || plan || 'Subscription';
  };

  const getScopeDisplayName = (scope) => {
    const scopeNames = {
      'router': 'CNC Router Edition',
      'laser': 'Laser Cutting Edition',
      'printing': '3D Printing Edition',
      'router+laser': 'CNC Router & Laser Cutting',
      'router+printing': 'CNC Router & 3D Printing',
      'laser+printing': 'Laser Cutting & 3D Printing',
      'all': 'All Editions (Full Access)'
    };
    return scopeNames[scope] || scope || 'All Features';
  };

  return (
    <div data-testid="subscription-success-page" className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-4">
      <Card className="bg-black/70 border-zinc-800 max-w-2xl w-full backdrop-blur-sm">
        <CardHeader>
          <div className="text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="w-16 h-16 text-[#e67e22] mx-auto mb-4 animate-spin" />
                <CardTitle className="text-2xl text-white mb-2">Processing Your Subscription</CardTitle>
                <CardDescription className="text-zinc-400">Please wait while we confirm your payment...</CardDescription>
              </>
            )}
            {status === 'success' && (
              <>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <CardTitle className="text-2xl text-white mb-2">Subscription Activated Successfully!</CardTitle>
                <CardDescription className="text-zinc-400">Welcome to CNC-Helpdesk. Your subscription is now active.</CardDescription>
              </>
            )}
            {status === 'error' && (
              <>
                <div className="w-16 h-16 rounded-full bg-yellow-900/20 border-2 border-yellow-500 flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl text-yellow-500">!</span>
                </div>
                <CardTitle className="text-2xl text-white mb-2">Payment Processing</CardTitle>
                <CardDescription className="text-zinc-400">
                  Your payment is being processed. You should receive confirmation shortly.
                </CardDescription>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {status === 'success' && (
            <>
              {/* Subscription Details */}
              <div className="bg-zinc-900/50 rounded-lg p-6 border border-green-500/30 space-y-4">
                {/* Plan Name */}
                <div className="flex items-center justify-center gap-2 text-lg font-semibold text-white">
                  <CreditCard className="w-5 h-5 text-[#e67e22]" />
                  {getPlanDisplayName(subscriptionData?.subscription_plan)}
                </div>
                
                {/* Status Badge */}
                <div className="flex items-center justify-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    subscriptionData?.subscription_status === 'active' 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  }`}>
                    {subscriptionData?.subscription_status?.toUpperCase() || 'ACTIVATING'}
                  </span>
                </div>

                {/* Access Scope */}
                {subscriptionData?.subscription_edition_scope && (
                  <div className="pt-4 border-t border-zinc-800">
                    <div className="text-sm text-zinc-400 mb-1">Your Access Includes</div>
                    <div className="text-white font-medium">
                      {getScopeDisplayName(subscriptionData.subscription_edition_scope)}
                    </div>
                  </div>
                )}

                {/* Renewal Date */}
                {subscriptionData?.subscription_renews_at && (
                  <div className="flex items-center justify-center gap-2 text-sm text-zinc-400">
                    <Calendar className="w-4 h-4" />
                    Renews: {formatDate(subscriptionData.subscription_renews_at)}
                  </div>
                )}
              </div>

              {/* Beta Notice */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <div className="flex items-center justify-center gap-2 text-green-400 mb-2">
                  <Shield className="w-5 h-5" />
                  <span className="font-semibold">Beta Access Active</span>
                </div>
                <p className="text-sm text-green-300/80">
                  All features are currently accessible during beta. Your subscription supports development
                  and guarantees your pricing when we exit beta.
                </p>
              </div>
              
              <Button
                data-testid="continue-dashboard-btn"
                onClick={handleContinue}
                className="w-full bg-[#e67e22] hover:bg-[#d35400] text-black font-bold uppercase tracking-wide py-6 text-lg"
              >
                Continue to Dashboard
              </Button>
            </>
          )}
          
          {status === 'error' && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                If you completed payment, your subscription will be activated automatically.
                Check your email for confirmation.
              </p>
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={handleContinue}
                  className="bg-[#e67e22] hover:bg-[#d35400] text-black font-bold"
                >
                  Go to Dashboard
                </Button>
                <Button
                  onClick={() => navigate('/pricing')}
                  variant="outline"
                  className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                >
                  Back to Pricing
                </Button>
              </div>
            </div>
          )}

          {status === 'loading' && (
            <p className="text-sm text-zinc-500">
              This usually takes just a moment...
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
